import apiClient from './apiClient';
import { ENDPOINTS } from './apiConfig';

/**
 * Obtiene el RIF de la empresa
 * Esta función ya no usa localStorage
 */
const getCompanyRif = () => {
    return null;
};

/**
 * Normaliza los datos de la empresa que vienen de la API
 * Asegura que los valores booleanos sean consistentes y mapea nombres de campos
 * @param {Object} apiData - Datos de la API
 * @returns {Object} - Datos normalizados
 */
const normalizeCompanyData = (apiData) => {
    if (!apiData) return null;

    return {
        ...apiData,
        // Asegurar que los booleanos sean strings "0" o "1"
        repetir_numero_control: apiData.repetir_numero_control || '0',
        aplica_orden_pago: apiData.aplica_orden_pago || '0',
        verificar_proveedor: apiData.verificar_proveedor || '0',

        // Mapear nombres de campos de la API a nombres usados en la app
        signature: apiData.ruta_firma || null,
        stamp: apiData.ruta_sello || null,
        // ruta_logo ya está correcto
    };
};

const companyService = {
    /**
     * Crear una nueva empresa
     * @param {Object} companyData - Datos de la empresa (ya en snake_case)
     * @returns {Promise<Object>} - Empresa creada
     */
    create: async (companyData) => {
        console.log('Creating company with data:', companyData);
        const response = await apiClient.post(ENDPOINTS.COMPANY.BASE, companyData);
        return normalizeCompanyData(response);
    },

    /**
     * Obtener detalles de la empresa por RIF
     * @param {string} rif - RIF de la empresa
     * @returns {Promise<Object>} - Datos de la empresa
     */
    getByRif: async (rif) => {
        const response = await apiClient.get(`${ENDPOINTS.COMPANY.BASE}/${rif}`);
        return normalizeCompanyData(response);
    },

    /**
     * Obtener todas las empresas
     * @returns {Promise<Array>} - Lista de empresas
     */
    getAll: async () => {
        const response = await apiClient.get(ENDPOINTS.COMPANY.BASE);
        // Si la respuesta es un array, normalizar cada elemento
        if (Array.isArray(response)) {
            return response.map(normalizeCompanyData);
        }
        // Si es un objeto único, devolverlo como array de un elemento
        return [normalizeCompanyData(response)];
    },

    /**
     * Obtener la empresa actual (primera empresa disponible)
     * @returns {Promise<Object>} - Datos de la empresa actual
     */
    getCurrent: async () => {
        const companies = await companyService.getAll();
        if (!companies || companies.length === 0) {
            throw new Error('No se encontraron empresas registradas');
        }
        return companies[0];
    },

    /**
     * Actualizar datos de la empresa
     * @param {string} rif - RIF de la empresa
     * @param {Object} companyData - Datos actualizados (ya en snake_case)
     * @returns {Promise<Object>} - Empresa actualizada
     */
    update: async (rif, companyData) => {
        console.log('Updating company with data:', companyData);
        const response = await apiClient.put(`${ENDPOINTS.COMPANY.BASE}/${rif}`, companyData);
        return normalizeCompanyData(response);
    },

    /**
     * Actualizar la empresa actual
     * @param {Object} companyData - Datos actualizados (ya en snake_case)
     * @returns {Promise<Object>} - Empresa actualizada
     */
    updateCurrent: async (companyData) => {
        const rif = getCompanyRif();
        if (!rif) {
            throw new Error('No se encontró el RIF de la empresa');
        }
        return companyService.update(rif, companyData);
    },
    /**
     * Obtener tasas de impuestos
     */
    getTaxRates: async (rif) => {
        const response = await apiClient.get(ENDPOINTS.COMPANY.TAX_RATES(rif));
        return response.data || response;
    },

    /**
     * Obtener otros porcentajes
     */
    getOtherPercentages: async (rif) => {
        const response = await apiClient.get(ENDPOINTS.COMPANY.OTHER_PERCENTAGES(rif));
        return response.data || response;
    },

    /**
     * Obtener unidad tributaria
     */
    getTaxUnit: async (rif) => {
        const response = await apiClient.get(ENDPOINTS.COMPANY.TAX_UNIT(rif));
        return response.data || response;
    },

    /**
     * Obtener configuraciones generales
     */
    getSettings: async (rif) => {
        const response = await apiClient.get(ENDPOINTS.COMPANY.SETTINGS(rif));
        return response.data || response;
    },

    /**
     * Cargar logo de la empresa
     * @param {string} rif 
     * @param {File} file 
     */
    uploadLogo: async (rif, file) => {
        const formData = new FormData();
        formData.append('logo', file);

        const response = await apiClient.post(ENDPOINTS.COMPANY.LOGO(rif), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data || response;
    },

    /**
     * Cargar firma de la empresa
     * @param {string} rif 
     * @param {File} file 
     */
    uploadSignature: async (rif, file) => {
        const formData = new FormData();
        formData.append('signature', file);

        const response = await apiClient.post(ENDPOINTS.COMPANY.SIGNATURE(rif), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data || response;
    },

    /**
     * Cargar sello de la empresa
     * @param {string} rif 
     * @param {File} file 
     */
    uploadSeal: async (rif, file) => {
        const formData = new FormData();
        formData.append('seal', file);

        const response = await apiClient.post(ENDPOINTS.COMPANY.SEAL(rif), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data || response;
    },

    /**
     * Cargar múltiples imágenes (logo, firma, sello)
     * @param {string} rif 
     * @param {Object} files - { logo, signature, seal }
     */
    uploadImages: async (rif, files) => {
        const formData = new FormData();
        if (files.logo) formData.append('logo', files.logo);
        if (files.signature) formData.append('signature', files.signature);
        if (files.seal) formData.append('seal', files.seal);

        const response = await apiClient.post(ENDPOINTS.COMPANY.IMAGES(rif), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data || response;
    },

    /**
     * Eliminar logo de la empresa (Actualizando con ruta_logo: null)
     * @param {string} rif 
     */
    deleteLogo: async (rif) => {
        console.log('Deleting logo via update for RIF:', rif);
        // Intentamos enviar null o cadena vacía para limpiar el campo
        return companyService.update(rif, { ruta_logo: null });
    },

    /**
     * Eliminar firma de la empresa (Actualizando con ruta_firma: null)
     * @param {string} rif 
     */
    deleteSignature: async (rif) => {
        console.log('Deleting signature via update for RIF:', rif);
        return companyService.update(rif, { ruta_firma: null });
    },

    /**
     * Eliminar sello de la empresa (Actualizando con ruta_sello: null)
     * @param {string} rif 
     */
    deleteSeal: async (rif) => {
        console.log('Deleting seal via update for RIF:', rif);
        return companyService.update(rif, { ruta_sello: null });
    }
};

export default companyService;
